// Code generated by scripts/contractgen from contracts/openapi/platform.openapi.json. DO NOT EDIT.
/**
 * POST /v1/auth/register — Create a suite account
 */
export function registerAccount(transport, input) {
    return transport.request({
        operationId: "registerAccount",
        method: "POST",
        path: "/v1/auth/register",
        body: input.body,
    });
}
/**
 * GET /v1/auth/registration/{userID} — Where a half-finished registration
 * got to
 */
export function getRegistrationStatus(transport, input) {
    return transport.request({
        operationId: "getRegistrationStatus",
        method: "GET",
        path: `/v1/auth/registration/${encodeURIComponent(String(input.userID))}`,
    });
}
/**
 * POST /v1/auth/otp/send — Send (or resend) the phone verification code.
 * 202, not 200: the platform has accepted responsibility for delivery, which
 * is not the same as the SMS having arrived.
 */
export function sendOTP(transport, input) {
    return transport.request({
        operationId: "sendOTP",
        method: "POST",
        path: "/v1/auth/otp/send",
        body: input.body,
    });
}
/**
 * POST /v1/auth/otp/verify — Prove possession of the phone number
 */
export function verifyOTP(transport, input) {
    return transport.request({
        operationId: "verifyOTP",
        method: "POST",
        path: "/v1/auth/otp/verify",
        body: input.body,
    });
}
/**
 * POST /v1/auth/sso/login — Start the suite browser session. The session
 * token is NEVER in the response body — it is set as an HttpOnly cookie,
 * so a script on the sign-in page cannot read it and a copy in JSON would
 * undo that.
 */
export function signIn(transport, input) {
    return transport.request({
        operationId: "signIn",
        method: "POST",
        path: "/v1/auth/sso/login",
        body: input.body,
    });
}
/**
 * POST /v1/auth/sso/logout — End the suite browser session
 */
export function signOut(transport) {
    return transport.request({
        operationId: "signOut",
        method: "POST",
        path: "/v1/auth/sso/logout",
    });
}
/**
 * GET /v1/auth/sso/session — Who is signed in
 */
export function getSession(transport) {
    return transport.request({
        operationId: "getSession",
        method: "GET",
        path: "/v1/auth/sso/session",
    });
}
/**
 * GET /v1/auth/account/profile — The signed-in user's profile and contact
 * points
 */
export function getProfile(transport) {
    return transport.request({
        operationId: "getProfile",
        method: "GET",
        path: "/v1/auth/account/profile",
    });
}
/**
 * GET /v1/auth/account/security-events — This account's security history
 */
export function listSecurityEvents(transport, input) {
    return transport.request({
        operationId: "listSecurityEvents",
        method: "GET",
        path: "/v1/auth/account/security-events",
        query: { "limit": input.limit },
    });
}
/**
 * GET /v1/auth/account/devices — Every installation bound to this account
 */
export function listDevices(transport) {
    return transport.request({
        operationId: "listDevices",
        method: "GET",
        path: "/v1/auth/account/devices",
    });
}
/**
 * POST /v1/auth/account/devices/{deviceID}/revoke — End every session and
 * binding on one installation
 */
export function revokeDevice(transport, input) {
    return transport.request({
        operationId: "revokeDevice",
        method: "POST",
        path: `/v1/auth/account/devices/${encodeURIComponent(String(input.deviceID))}/revoke`,
    });
}
/**
 * POST /v1/auth/account/devices/{deviceID}/retire — Retire an installation
 * this account no longer has. The destructive counterpart to /revoke, for a
 * machine that is lost or gone. It ends the caller's bindings, sessions and
 * refresh families on the installation AND retires the device row and its
 * key, so nobody can sign in on the machine until it is restored. Requires a
 * LIVE binding: only the installation's current occupant may retire it,
 * which is what stops one person ending a shared machine somebody else is
 * using. Reversible by the same account through /restore — that is the
 * whole difference from an operator's block, which is permanent.
 */
export function retireDevice(transport, input) {
    return transport.request({
        operationId: "retireDevice",
        method: "POST",
        path: `/v1/auth/account/devices/${encodeURIComponent(String(input.deviceID))}/retire`,
    });
}
/**
 * POST /v1/auth/account/devices/{deviceID}/restore — Bring back an
 * installation this account retired. Reopens the device row and the key that
 * /retire took down, and nothing else. Bindings, sessions and refresh
 * families deliberately do NOT come back — the next sign-in re-establishes
 * the binding through the normal registration path, after the client has
 * proved it still holds the key. Only the account that retired an
 * installation may restore it, and only a retire may be undone: one an
 * operator blocked answers 404, identically to one that does not exist, so
 * this endpoint cannot be used to find out which machines are blocked. Offer
 * it only when the device reports `restorable`.
 */
export function restoreDevice(transport, input) {
    return transport.request({
        operationId: "restoreDevice",
        method: "POST",
        path: `/v1/auth/account/devices/${encodeURIComponent(String(input.deviceID))}/restore`,
    });
}
/**
 * POST /v1/auth/account/bindings/{bindingID}/revoke — Unbind one
 * application from one installation
 */
export function revokeBinding(transport, input) {
    return transport.request({
        operationId: "revokeBinding",
        method: "POST",
        path: `/v1/auth/account/bindings/${encodeURIComponent(String(input.bindingID))}/revoke`,
    });
}
/**
 * GET /v1/auth/account/sessions — Every application session on this
 * account
 */
export function listSessions(transport) {
    return transport.request({
        operationId: "listSessions",
        method: "GET",
        path: "/v1/auth/account/sessions",
    });
}
/**
 * POST /v1/auth/account/sessions/{sessionID}/revoke — End one application
 * session
 */
export function revokeSession(transport, input) {
    return transport.request({
        operationId: "revokeSession",
        method: "POST",
        path: `/v1/auth/account/sessions/${encodeURIComponent(String(input.sessionID))}/revoke`,
    });
}
/**
 * GET /v1/auth/account/device-approvals — Devices waiting to be approved
 * on this account
 */
export function listDeviceApprovals(transport) {
    return transport.request({
        operationId: "listDeviceApprovals",
        method: "GET",
        path: "/v1/auth/account/device-approvals",
    });
}
/**
 * POST /v1/auth/account/device-approvals/{approvalID}/approve — Approve a
 * new installation
 */
export function approveDevice(transport, input) {
    return transport.request({
        operationId: "approveDevice",
        method: "POST",
        path: `/v1/auth/account/device-approvals/${encodeURIComponent(String(input.approvalID))}/approve`,
    });
}
/**
 * POST /v1/auth/account/device-approvals/{approvalID}/deny — Deny a new
 * installation, revoking its binding
 */
export function denyDevice(transport, input) {
    return transport.request({
        operationId: "denyDevice",
        method: "POST",
        path: `/v1/auth/account/device-approvals/${encodeURIComponent(String(input.approvalID))}/deny`,
    });
}
/**
 * POST /v1/app/{application}/session/sign-out — Release this installation
 * from the signed-in account. Called by the application ON the machine, with
 * that machine's own key. It ends the caller's bindings, sessions and
 * refresh families on this installation across EVERY application, and
 * deliberately leaves auth.devices and its public key active so the next
 * person can sign in on the same machine — a device is an installation,
 * not a possession. There is no request body, and there must never be one:
 * the blast radius of a sign-out must not be caller-controlled. The device
 * is read from the binding the token was minted against, never from the path
 * or a body, so there is nothing to own and nothing to enumerate. The
 * browser-side revoke on this same account (POST
 * /v1/auth/account/devices/{deviceID}/revoke) is the surface for a laptop
 * that is gone; this one is the convenience for whoever is holding it.
 */
export function signOutDevice(transport, input) {
    return transport.request({
        operationId: "signOutDevice",
        method: "POST",
        path: `/v1/app/${encodeURIComponent(String(input.application))}/session/sign-out`,
    });
}
/**
 * POST /v1/app/{application}/device/version — Report this installation's
 * app and OS version. Sent on application start. It may move only
 * app_version, os_version and device_name — never the installation id, the
 * platform, the key or the stable attributes, all of which are checksummed:
 * anything checksummed this route could write would be a self-service way
 * for a client to force its own sign-out. A report that changes nothing is
 * free and audits nothing, so a client may call it on every launch. A report
 * that DOES change something is rate limited, which is the 429.
 */
export function reportDeviceVersion(transport, input) {
    return transport.request({
        operationId: "reportDeviceVersion",
        method: "POST",
        path: `/v1/app/${encodeURIComponent(String(input.application))}/device/version`,
        body: input.body,
    });
}
/**
 * POST /v1/app/{application}/device/metadata — File whatever this
 * installation holds about itself. A place to put what the platform did not
 * think to ask for: a licence tier, a plugin inventory, a GPU list. The
 * payload is an arbitrary JSON OBJECT whose shape this platform never
 * interprets — it is stored verbatim and rendered back to an operator —
 * so a client release may change it without a server change. The device, the
 * user and the application are taken from the authenticated session and
 * CANNOT be named in the body: there is no device_id, user_id or
 * application_id field, and sending one is refused rather than ignored. A
 * service credential is refused outright, because a machine caller has no
 * device whose metadata it could be reporting. Reports DEDUPLICATE. Two
 * spellings of one document — different member order, different whitespace
 * — are one row, and re-sending it bumps `report_count` and answers
 * `deduplicated: true` rather than filing it again. That is what makes
 * reporting on every launch cheap, and it is why the hourly ceiling counts
 * only documents that are NEW to the device: a repeat costs nothing and is
 * never rate limited.
 */
export function reportDeviceMetadata(transport, input) {
    return transport.request({
        operationId: "reportDeviceMetadata",
        method: "POST",
        path: `/v1/app/${encodeURIComponent(String(input.application))}/device/metadata`,
        body: input.body,
    });
}
/**
 * GET /platform/v1/applications — The applications this customer holds a
 * wallet for. The one control-plane read scoped to the SUBJECT rather than
 * to a single wallet: it is how a browser learns the application ids every
 * other route in this tag takes. One entry per WALLET, so an application
 * funded in two currencies appears once per currency; an application another
 * customer funded never joins the query and is absent rather than filtered.
 */
export function listApplications(transport) {
    return transport.request({
        operationId: "listApplications",
        method: "GET",
        path: "/platform/v1/applications",
    });
}
/**
 * GET /platform/v1/applications/{applicationID}/wallet — One application's
 * wallet position. The application id is a FILTER, not authority: the wallet
 * is derived from the signed-in subject, so an id belonging to somebody
 * else's application answers 404.
 */
export function getWallet(transport, input) {
    return transport.request({
        operationId: "getWallet",
        method: "GET",
        path: `/platform/v1/applications/${encodeURIComponent(String(input.applicationID))}/wallet`,
        query: { "currency": input.currency },
    });
}
/**
 * GET /platform/v1/applications/{applicationID}/usage — What this
 * application actually spent
 */
export function listUsage(transport, input) {
    return transport.request({
        operationId: "listUsage",
        method: "GET",
        path: `/platform/v1/applications/${encodeURIComponent(String(input.applicationID))}/usage`,
        query: { "currency": input.currency, "limit": input.limit, "offset": input.offset },
    });
}
/**
 * GET /platform/v1/applications/{applicationID}/transactions — Ledger
 * movements on this wallet
 */
export function listTransactions(transport, input) {
    return transport.request({
        operationId: "listTransactions",
        method: "GET",
        path: `/platform/v1/applications/${encodeURIComponent(String(input.applicationID))}/transactions`,
        query: { "currency": input.currency, "limit": input.limit, "offset": input.offset },
    });
}
/**
 * GET /platform/v1/applications/{applicationID}/invoices — Top-up receipts
 * for this wallet
 */
export function listInvoices(transport, input) {
    return transport.request({
        operationId: "listInvoices",
        method: "GET",
        path: `/platform/v1/applications/${encodeURIComponent(String(input.applicationID))}/invoices`,
        query: { "currency": input.currency, "limit": input.limit, "offset": input.offset },
    });
}
/**
 * GET /platform/v1/applications/{applicationID}/packages — The
 * subscription plans on sale for one application. Only `active` packages,
 * ordered by the operator's sort order and then by price. The application id
 * is a FILTER, not authority, exactly as on the wallet routes: a customer
 * who holds no wallet for it gets 404 rather than an empty list, because an
 * empty list is a claim about the application and answering it would confirm
 * the application exists. Only plans priced in the wallet's currency appear
 * — a subscription is pinned to one wallet, one application and one
 * currency, so a plan in another currency could not be subscribed to.
 */
export function listPackages(transport, input) {
    return transport.request({
        operationId: "listPackages",
        method: "GET",
        path: `/platform/v1/applications/${encodeURIComponent(String(input.applicationID))}/packages`,
        query: { "currency": input.currency },
    });
}
/**
 * GET /platform/v1/applications/{applicationID}/subscription — The
 * customer's current subscription for this application. 200 with a null
 * `subscription` when the customer has none — not 404 — so a portal can
 * render the plans page without special-casing an error. The subscription
 * reported is the LIVE one (incomplete, active or past_due): a renewal being
 * retried has not ended, and showing nothing until it succeeds would tell a
 * customer they had been cancelled. It never carries a card number, a
 * provider identifier or a raw webhook payload; what a browser gets is the
 * plan, the terms it was sold at, the period, and a card brand with four
 * digits. An application this customer holds no wallet for is 404, exactly
 * as on the wallet and plans routes.
 */
export function getSubscription(transport, input) {
    return transport.request({
        operationId: "getSubscription",
        method: "GET",
        path: `/platform/v1/applications/${encodeURIComponent(String(input.applicationID))}/subscription`,
        query: { "currency": input.currency },
    });
}
/**
 * POST /platform/v1/applications/{applicationID}/subscription — Start a
 * subscription to one of this application's plans. Writes the subscription
 * as `incomplete` and activates NOTHING: the URL that comes back is a hosted
 * page, and only a verified `invoice.paid` webhook — through the worker
 * — grants the first cycle's allowance. 201 for a page that was opened,
 * 200 for an idempotent replay of one that already was. The plan must be
 * `active` and sold by the addressed application, and the account must not
 * already hold a live subscription for it. The wallet is created if this
 * customer has none for the application yet and holds an entitlement to it;
 * an application they hold neither for is a 404, exactly as on the plans
 * list.
 */
export function startSubscription(transport, input) {
    return transport.request({
        operationId: "startSubscription",
        method: "POST",
        path: `/platform/v1/applications/${encodeURIComponent(String(input.applicationID))}/subscription`,
        body: input.body,
    });
}
/**
 * DELETE /platform/v1/applications/{applicationID}/subscription — Stop the
 * subscription at the end of the period already paid for. Sets
 * `cancel_at_period_end` through the provider and reflects it locally. It
 * does NOT end access today: the allowance for the period already paid for
 * stays spendable until `access_ends_at`, because ending it now would either
 * strand credit the customer bought or need a refund this platform cannot
 * issue. Cancelling an already-cancelling subscription succeeds and changes
 * nothing. 200 rather than 204 because the date access ends is the one thing
 * the customer wants back. A subscription whose hosted page was never
 * completed has no provider object and no paid period, so it ends
 * immediately and `access_ends_at` is absent.
 */
export function cancelSubscription(transport, input) {
    return transport.request({
        operationId: "cancelSubscription",
        method: "DELETE",
        path: `/platform/v1/applications/${encodeURIComponent(String(input.applicationID))}/subscription`,
        query: { "currency": input.currency },
    });
}
/**
 * POST /platform/v1/top-ups/checkout — Open a hosted checkout for one
 * application wallet. 201 for a page that was opened, 200 for an idempotent
 * replay of one that already was. Credits land in the wallet named by
 * application_id and are never transferable to another application.
 */
export function startTopUpCheckout(transport, input) {
    return transport.request({
        operationId: "startTopUpCheckout",
        method: "POST",
        path: "/platform/v1/top-ups/checkout",
        body: input.body,
    });
}
//# sourceMappingURL=platform.js.map